import type { Project } from '../../api/types'
import type { PayApp } from '../../api/types'
import type { ChangeOrderLogEntry } from '../../api/projects'
import type { PulseEvent, PulsePaymentsPayload } from './pulseApi'
import { timelinePaymentChip } from './pulseTimelinePay.js'
import {
  pulseTimelineTitle,
  pulseTimelineMeta,
  pulseTimelineAmountDisplay,
  pulseCoContractFlow,
  pulseCoBillingLink,
} from './pulseTimelineCopy'
import PulseActor from './PulseActor'

const TYPE_FILTERS = [
  { id: '', label: 'All' },
  { id: 'billing', label: 'Billing' },
  { id: 'payment', label: 'Payment' },
  { id: 'change_order', label: 'Change order' },
  { id: 'document', label: 'Document' },
  { id: 'edit', label: 'Edit' },
  { id: 'contract', label: 'Contract' },
  { id: 'closeout', label: 'Closeout' },
]

function FlowArrow() {
  return (
    <svg className="pulse-contract-arrow" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function ContractFlow({
  flow,
}: {
  flow: NonNullable<ReturnType<typeof pulseCoContractFlow>>
}) {
  const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  if (flow.kind === 'original') {
    return (
      <div className="pulse-contract-flow" aria-label="Original contract amount">
        <span className="pulse-contract-step pulse-contract-step--current">
          <small>Original contract amount</small>
          <b>{fmt(flow.amount)}</b>
        </span>
      </div>
    )
  }
  const delta = flow.delta < 0 ? `−${fmt(Math.abs(flow.delta))}` : `+${fmt(flow.delta)}`
  return (
    <div className="pulse-contract-flow" aria-label="Contract amount change">
      <span className="pulse-contract-step">
        <small>Previous contract</small>
        <b>{fmt(flow.previous)}</b>
      </span>
      <span className="pulse-contract-plus" aria-hidden="true">+</span>
      <span className="pulse-contract-step">
        <small>CO-{flow.coNumber}</small>
        <b>{delta}</b>
      </span>
      <FlowArrow />
      <span className="pulse-contract-step pulse-contract-step--current">
        <small>New contract</small>
        <b>{fmt(flow.current)}</b>
      </span>
    </div>
  )
}

export function PulseFilters(props: {
  q: string
  types: string
  order: 'desc' | 'asc'
  since: string
  showSince: boolean
  onQ: (v: string) => void
  onTypes: (v: string) => void
  onOrder: (v: 'desc' | 'asc') => void
  onSince: (v: string) => void
  onExport: () => void
  onReset: () => void
}) {
  return (
    <div className="pulse-filters" role="search">
      <input
        aria-label="Search timeline"
        data-testid="pulse-search"
        value={props.q}
        onChange={(e) => props.onQ(e.target.value)}
        placeholder="Search project activity…"
      />
      <div className="pulse-chips" aria-label="Activity category">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.id || 'all'}
            type="button"
            aria-pressed={props.types === f.id}
            onClick={() => props.onTypes(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <select className="pulse-sr-only" aria-label="Type filter" data-testid="pulse-type-filter" value={props.types} onChange={(e) => props.onTypes(e.target.value)}>
        {TYPE_FILTERS.map((f) => <option key={f.id || 'all'} value={f.id}>{f.label}</option>)}
      </select>
      <select aria-label="Sort order" data-testid="pulse-order" value={props.order} onChange={(e) => props.onOrder(e.target.value as 'desc' | 'asc')}>
        <option value="desc">Newest</option>
        <option value="asc">Oldest</option>
      </select>
      {props.showSince && (
        <input
          type="datetime-local"
          aria-label="What changed since"
          data-testid="pulse-since"
          value={props.since}
          onChange={(e) => props.onSince(e.target.value)}
        />
      )}
      <button type="button" className="btn b-ol btn-sm" data-testid="pulse-export" onClick={props.onExport}>Export CSV</button>
      <button type="button" className="btn b-ol btn-sm" data-testid="pulse-reset" onClick={props.onReset}>Reset</button>
    </div>
  )
}

export default function PulseTimeline({
  events,
  payments,
  payApps,
  coEntries,
  project,
  onOpen,
  onOpenPayApp,
  onReset,
}: {
  events: PulseEvent[]
  payments?: PulsePaymentsPayload | null
  payApps?: PayApp[]
  coEntries?: ChangeOrderLogEntry[]
  project?: Project | null
  onOpen: (event: PulseEvent) => void
  onOpenPayApp?: (payAppId: number) => void
  onReset: () => void
}) {
  const ctx = { coEntries, payApps, payments, project }

  if (!events.length) {
    return (
      <div className="pulse-empty" data-testid="pulse-empty">
        <p>No events in this view.</p>
        <button type="button" className="btn b-ol btn-sm" data-testid="pulse-empty-reset" onClick={onReset}>Reset</button>
      </div>
    )
  }
  return (
    <ol className="pulse-tl" data-testid="pulse-timeline">
      {events.map((e) => {
        const pay = timelinePaymentChip(e, payments)
        const title = pulseTimelineTitle(e, ctx)
        const meta = pulseTimelineMeta(e)
        const amount = pulseTimelineAmountDisplay(e, ctx)
        const flow = pulseCoContractFlow(e, ctx)
        const billingLink = pulseCoBillingLink(e, ctx)
        const isPayment = e.type === 'payment_recorded' || e.type === 'retainage_release_paid'
        return (
          <li key={e.id}>
            <div
              className="pulse-row"
              role="button"
              tabIndex={0}
              data-type={e.type}
              data-testid={`pulse-row-${e.id}`}
              onClick={() => onOpen(e)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault()
                  onOpen(e)
                }
              }}
            >
              <span className="pulse-row-date" title={pay.history ? 'Entered from history' : undefined}>{e.display_label || e.display_date}</span>
              <span className="pulse-row-icon" aria-hidden="true">
                <svg className="pulse-icon-doc" viewBox="0 0 24 24"><path d="M14 3H5v18h14V8l-5-5v5h5M8 12h8M8 16h5" /></svg>
                <svg className="pulse-icon-cash" viewBox="0 0 24 24"><path d="M3 6h18v12H3zM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0" /></svg>
                <svg className="pulse-icon-plus" viewBox="0 0 24 24"><path d="M5 12h14M12 5v14" /></svg>
                <svg className="pulse-icon-check" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></svg>
              </span>
              <span className="pulse-row-body">
                <div className="pulse-row-title">{title}</div>
                {flow ? <ContractFlow flow={flow} /> : null}
                {billingLink && onOpenPayApp ? (
                  <button
                    type="button"
                    className={`pulse-billing-link${billingLink.partial ? ' pulse-billing-link--partial' : ''}`}
                    data-testid={`pulse-billing-link-${e.id}`}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      onOpenPayApp(billingLink.payAppId)
                    }}
                  >
                    {billingLink.label} · View pay app
                  </button>
                ) : null}
                <div className="pulse-row-meta">
                  <span className="pulse-row-tag">{meta}</span>
                  <PulseActor name={e.actor} />
                </div>
              </span>
              <span className={`pulse-row-amt${isPayment ? ' pulse-row-amt--pay' : ''}`}>
                <span className="pulse-row-amt-value">{amount}</span>
                {pay.chip ? (
                  <span className={`pulse-pay-chip pulse-pay-chip-${pay.chip.kind}`} data-testid="pulse-pay-chip" data-kind={pay.chip.kind}>
                    {pay.chip.label}
                  </span>
                ) : null}
              </span>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
